/* eslint-disable max-lines */
import { UUID } from "crypto";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAgentApi } from "@/lib/api/agent/useAgentApi";
import { CreateKnowledgeBaseInput } from "@/lib/api/brain/types";
import { useKnowledgeBaseApi } from "@/lib/api/brain/useKnowledgeBaseApi";
import { useToast } from "@/lib/hooks";
import { Prompt } from "@/lib/types/Prompt";

import { MinimalKnowledgeBaseForUser } from "../types";

export const useKnowledgeBaseProvider = () => {
  const { publish } = useToast();
  const { createKnowledgeBase, deleteKnowledgeBase, getKnowledgeBases } =
    useKnowledgeBaseApi();
  const { getAgents } = useAgentApi();
  const { t } = useTranslation(["delete_or_unsubscribe_from_brain"]);

  const [allKnowledgeBases, setAllKnowledgeBases] = useState<
    MinimalKnowledgeBaseForUser[]
  >([]);
  const [currentKnowledgeBaseId, setCurrentKnowledgeBaseId] =
    useState<null | UUID>(null);
  const [isFetchingKnowledgeBases, setIsFetchingKnowledgeBases] =
    useState(true);
  const [publicPrompts] = useState<Prompt[]>([]);
  const [currentPromptId, setCurrentPromptId] = useState<null | string>(null);

  const currentPrompt = publicPrompts.find(
    (prompt) => prompt.id === currentPromptId,
  );
  const currentKnowledgeBase = allKnowledgeBases.find(
    (kb) => kb.id === currentKnowledgeBaseId,
  );

  const fetchAllKnowledgeBases = useCallback(async () => {
    setIsFetchingKnowledgeBases(true);
    try {
      const knowledgeBases = await getKnowledgeBases();

      setAllKnowledgeBases([...knowledgeBases]);
    } catch (error) {
      console.error(error);
    } finally {
      setIsFetchingKnowledgeBases(false);
    }
  }, [getKnowledgeBases, getAgents]);

  const createKnowledgeBaseHandler = useCallback(
    async (
      knowledgeBase: CreateKnowledgeBaseInput,
    ): Promise<UUID | undefined> => {
      const createdKnowledgeBase = await createKnowledgeBase(knowledgeBase);
      try {
        setCurrentKnowledgeBaseId(createdKnowledgeBase.id);

        void fetchAllKnowledgeBases();

        return createdKnowledgeBase.id;
      } catch {
        publish({
          variant: "danger",
          text: "Error occurred while creating a knowledge base",
        });
      }
    },
    [createKnowledgeBase, fetchAllKnowledgeBases, publish],
  );

  const deleteKnowledgeBaseHandler = useCallback(
    async (id: UUID) => {
      await deleteKnowledgeBase(id);
      setAllKnowledgeBases((prevKnowledgeBases) =>
        prevKnowledgeBases.filter((kb) => kb.id !== id),
      );
      publish({
        variant: "success",
        text: t("successfully_deleted"),
      });
    },
    [deleteKnowledgeBase, publish],
  );

  return {
    allKnowledgeBases,
    fetchAllKnowledgeBases,
    isFetchingKnowledgeBases,
    currentKnowledgeBase,
    currentKnowledgeBaseId,
    setCurrentKnowledgeBaseId,
    currentPrompt,
    setCurrentPromptId,
    currentPromptId,
    createKnowledgeBaseHandler,
    deleteKnowledgeBaseHandler,
  };
};
